import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { ActionItemsTab } from '../components/meetings/ActionItemsTab';

// ── TypeScript Interfaces ─────────────────────────────────────

type MeetingType = 'Committee' | 'GeneralBody';
type MeetingStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled';
type MeetingTab = 'agenda' | 'minutes' | 'attendees' | 'action-items';

interface MeetingDetail {
  id: string;
  title: string;
  description: string | null;
  meetingDate: string;
  location: string | null;
  meetingType: MeetingType;
  status: MeetingStatus;
  subcommittee: { id: string; name: string } | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ── Constants ─────────────────────────────────────────────────

const STATUS_COLORS: Record<MeetingStatus, string> = {
  Scheduled: '#1976d2',
  InProgress: '#f57c00',
  Completed: '#1a6b3c',
  Cancelled: '#d32f2f',
};

const TAB_LABELS: { key: MeetingTab; label: string }[] = [
  { key: 'agenda', label: 'Agenda' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'attendees', label: 'Attendees' },
  { key: 'action-items', label: 'Action Items' },
];

/**
 * Valid status transitions per the meeting lifecycle:
 * Scheduled → InProgress, Scheduled → Cancelled
 * InProgress → Completed, InProgress → Cancelled
 */
const VALID_TRANSITIONS: Record<MeetingStatus, { label: string; target: MeetingStatus }[]> = {
  Scheduled: [
    { label: 'Start Meeting', target: 'InProgress' },
    { label: 'Cancel', target: 'Cancelled' },
  ],
  InProgress: [
    { label: 'Complete', target: 'Completed' },
    { label: 'Cancel', target: 'Cancelled' },
  ],
  Completed: [],
  Cancelled: [],
};

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#1a6b3c', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '1rem', textDecoration: 'none', fontWeight: 500 },
  headerSection: { marginBottom: '1.5rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  titleRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { color: '#1a6b3c', fontSize: '1.5rem', margin: 0 },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  metaRow: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.75rem', fontSize: '0.85rem', color: '#555' },
  metaItem: { display: 'flex', alignItems: 'center', gap: '0.3rem' },
  controls: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '0.5rem 1rem', backgroundColor: '#fff', color: '#1a6b3c', border: '1px solid #1a6b3c', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnTransition: { padding: '0.4rem 0.75rem', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  tabBar: { display: 'flex', gap: 0, borderBottom: '2px solid #e0e0e0', marginBottom: '1.5rem' },
  tab: { padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: '#666', borderBottom: '2px solid transparent', marginBottom: '-2px', transition: 'color 0.15s, border-color 0.15s' },
  tabActive: { padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', border: 'none', backgroundColor: 'transparent', color: '#1a6b3c', borderBottom: '2px solid #1a6b3c', marginBottom: '-2px' },
  tabContent: { padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minHeight: '200px' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  placeholder: { textAlign: 'center' as const, padding: '2rem', color: '#999', fontSize: '0.9rem' },
};

// ── Component ─────────────────────────────────────────────────

export function MeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
  const [activeTab, setActiveTab] = useState<MeetingTab>('agenda');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transitioning, setTransitioning] = useState(false);

  const canEdit = user ? isAtLeast(user.role, 'Organizer') : false;

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    if (id) {
      fetchMeeting();
    }
  }, [id]);

  async function fetchMeeting() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<MeetingDetail>(`/meetings/${id}`);
      setMeeting(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load meeting details.');
    } finally {
      setLoading(false);
    }
  }

  // ── Actions ───────────────────────────────────────────────

  async function handleStatusTransition(targetStatus: MeetingStatus) {
    if (!meeting) return;
    setTransitioning(true);
    try {
      const updated = await apiClient<MeetingDetail>(`/meetings/${meeting.id}`, {
        method: 'PATCH',
        body: { status: targetStatus },
      });
      setMeeting(updated);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update meeting status.');
    } finally {
      setTransitioning(false);
    }
  }

  async function handleDelete() {
    if (!meeting) return;
    const confirmed = window.confirm(`Are you sure you want to delete "${meeting.title}"? This will remove all associated data.`);
    if (!confirmed) return;
    try {
      await apiClient(`/meetings/${meeting.id}`, { method: 'DELETE' });
      navigate('/meetings');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete meeting.');
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day); // Local date, no UTC shift
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function formatTime(dateStr: string): string {
    if (!dateStr) return '';
    // Extract HH:MM from stored value (stored as Eastern, no conversion needed)
    let hours: number;
    let minutes: number;

    if (dateStr.includes('T')) {
      const timePart = dateStr.split('T')[1].slice(0, 5);
      [hours, minutes] = timePart.split(':').map(Number);
    } else if (dateStr.includes(':')) {
      [hours, minutes] = dateStr.split(':').map(Number);
    } else {
      return dateStr;
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  // ── Tab Content Renderers ─────────────────────────────────

  function renderTabContent() {
    switch (activeTab) {
      case 'agenda':
        return <div style={styles.placeholder}>Agenda items will be displayed here.</div>;
      case 'minutes':
        return <div style={styles.placeholder}>Meeting minutes will be displayed here.</div>;
      case 'attendees':
        return <div style={styles.placeholder}>Attendee list will be displayed here.</div>;
      case 'action-items':
        return (
          <ActionItemsTab
            meetingId={meeting!.id}
            subcommitteeId={meeting!.subcommittee?.id || null}
            canEdit={canEdit}
          />
        );
      default:
        return null;
    }
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Loading meeting details...</div>
      </div>
    );
  }

  if (error && !meeting) {
    return (
      <div style={styles.page}>
        <div style={styles.error}>{error}</div>
        <button style={styles.btn} onClick={() => navigate('/meetings')}>
          ← Back to Meetings
        </button>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div style={styles.page}>
        <div style={styles.error}>Meeting not found.</div>
        <button style={styles.btn} onClick={() => navigate('/meetings')}>
          ← Back to Meetings
        </button>
      </div>
    );
  }

  const transitions = VALID_TRANSITIONS[meeting.status];

  return (
    <div style={styles.page}>
      {/* Back link */}
      <a style={styles.backLink} onClick={() => navigate('/meetings')}>
        ← Back to Meetings
      </a>

      {/* Error banner */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerTop}>
          <div>
            <div style={styles.titleRow}>
              <h1 style={styles.title}>{meeting.title}</h1>
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: STATUS_COLORS[meeting.status],
                }}
              >
                {meeting.status}
              </span>
            </div>

            <div style={styles.metaRow}>
              <span style={styles.metaItem}>
                📅 {formatDate(meeting.meetingDate)}
              </span>
              <span style={styles.metaItem}>
                🕐 {formatTime(meeting.meetingDate)}
              </span>
              {meeting.location && (
                <span style={styles.metaItem}>
                  📍 {meeting.location}
                </span>
              )}
              <span style={styles.metaItem}>
                🏛️ {meeting.subcommittee?.name || 'General Body'}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div style={styles.controls}>
            {/* Status transition buttons */}
            {canEdit && transitions.length > 0 && transitions.map((t) => (
              <button
                key={t.target}
                style={t.target === 'Cancelled' ? { ...styles.btnTransition, backgroundColor: '#d32f2f' } : styles.btnTransition}
                onClick={() => handleStatusTransition(t.target)}
                disabled={transitioning}
              >
                {t.label}
              </button>
            ))}

            {/* Edit / Delete buttons */}
            {canEdit && meeting.status !== 'Cancelled' && (
              <>
                <button
                  style={styles.btnSecondary}
                  onClick={() => navigate(`/meetings/${meeting.id}/edit`)}
                >
                  Edit
                </button>
                <button
                  style={styles.btnDanger}
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {TAB_LABELS.map((tab) => (
          <button
            key={tab.key}
            style={activeTab === tab.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.key)}
            aria-selected={activeTab === tab.key}
            role="tab"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent} role="tabpanel">
        {renderTabContent()}
      </div>
    </div>
  );
}
